import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { Box, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button, COLORS, Icon, ICONS, Modal } from "abs-ui-components";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { API_ROUTES } from "../../FeuApiRoutes/FeuApiRoutes";

const FeuB5ZeroEmission = forwardRef(({ b5ZeroEmission, refetchMonitoringData, shouldRefetch, b5ZeroEmissiondropdown,vesselMstId }, ref) => {

	const [dropdownOptions, setDropdownOptions] = useState({ zetTypes: [] });
	const [zetData, setZetData] = useState([]);
	const [validationErrors, setValidationErrors] = useState({});
	const [activeAction, setActiveAction] = useState(-1);
	const RowActions = { Edit: 0, Add: 1, Delete: 2 };
	const [hasFetched, setHasFetched] = useState(false);
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [deletingRowIndex, setDeletingRowIndex] = useState();

	useEffect(() => {
		const cleanedData = (b5ZeroEmission || []).map((item) => ({
			zetType: item.zetType ?? "",
			zetName: item.zetName ?? "",
			serialNumber: item.serialNumber ?? "",
			descriptionOfZet: item.descriptionOfZet ?? "",
		}));
		setZetData(cleanedData);
	}, [b5ZeroEmission]);

	useEffect(() => {
		if (!b5ZeroEmissiondropdown) return;
		setDropdownOptions({
			zetTypes: (b5ZeroEmissiondropdown?.zetTypes || []).map((item) => ({
				label: item.value,
				value: item.code,
			})),
		});
	}, [b5ZeroEmissiondropdown]);

	useEffect(() => {
		if (shouldRefetch && !hasFetched) setHasFetched(true);
	}, [shouldRefetch]);

	useEffect(() => {
		if (!shouldRefetch) setHasFetched(false);
	}, [shouldRefetch]);

	const resetActiveRow = () => {
		setActiveAction(-1);
		setValidationErrors({});
	};

	const validation = (value) => (value?.length <= 1000 ? value : value.slice(0, 1000));

	const handleFuelActions = (table, row, action) => {
		if (activeAction === -1) {
			if (action === RowActions.Edit) table.setEditingRow(row);
			else if (action === RowActions.Add) table.setCreatingRow(true);
			setActiveAction(action);
		}
	};

	const handleDeleteBtnClick = (rowIndex) => {
		setConfirmDelete(true);
		setDeletingRowIndex(rowIndex);
	};


	const handleDeleteZet = async (rowIndex) => {
		const updatedData = zetData.filter((_, index) => index !== rowIndex);
		try {
			const res = await callApi({
				method: "POST",
				url: API_ROUTES.B5(vesselMstId),
				data: updatedData,
			});
			if (res?.isSuccess) {
				emitToasterMsg("success", "Table B.5", "ZET deleted successfully");
				setZetData(updatedData);
				setConfirmDelete(false);
				setActiveAction(-1);
				await refetchMonitoringData?.();
			} else {
				emitToasterMsg("error", "Table B.5", res?.errorMessage);
				setConfirmDelete(false);
			}
		} catch (error) {
			emitToasterMsg("error", "Table B.5", error.errorMessage);
		}
	};

	const validateZET = (values) => {
		const errors = {};
		if (!values.zetType) errors.zetType = "Required";
		return errors;
	};

	const createZET = async (newEntry, isAuto) => {
		const payload = [...zetData, newEntry];
		try {
			const res = await callApi({
				method: "POST",
				url: API_ROUTES.B5(vesselMstId),
				data: payload,
			});
			if (res?.isSuccess) {
				emitToasterMsg("success", "Table B.5", isAuto ? "ZET autosaved successfully" : "ZET added successfully");
				table.setCreatingRow(null);
				setZetData(payload);
				setActiveAction(-1);
				setValidationErrors({});
				await refetchMonitoringData?.();
			} else {
				emitToasterMsg("error", "Table B.5", res?.errorMessage);
			}
		} catch (error) {
			emitToasterMsg("error", "Table B.5", error.errorMessage);
		}
	};

	const updateZET = async (rowIndex, updatedEntry, isAuto) => {
		const payload = zetData.map((item, index) =>
			index === rowIndex ? { ...item, ...updatedEntry } : item
		);
		try {
			const res = await callApi({
				method: "POST",
				url: API_ROUTES.B5(vesselMstId),
				data: payload,
			});
			if (res?.isSuccess) {
				emitToasterMsg("success", "Table B.5", isAuto ? "ZET autosaved successfully" : "ZET updated successfully");
				table.setEditingRow(null);
				setZetData(payload);
				setActiveAction(-1);
				setValidationErrors({});
				await refetchMonitoringData?.();
			} else {
				emitToasterMsg("error", "Table B.5", res?.errorMessage);
			}
		} catch (error) {
			emitToasterMsg("error", "Table B.5", error.errorMessage);
		}
	};

	useImperativeHandle(ref, () => ({
		async submitAndCheck() {
			const { creatingRow, editingRow } = table.getState();
			if (!creatingRow && !editingRow) return true;
			const mode = creatingRow ? "add" : "edit";
			const editedValues = creatingRow ? creatingRow?._valuesCache : editingRow?._valuesCache;
			let isDirty = false;
			if (editingRow && editedValues) {
				isDirty = Object.keys(editedValues).some(
					(key) => editedValues[key] !== editingRow.original[key]
				);
			}
			const validationRes = validateZET(editedValues, mode, {});
			const hasErrors = Object.values(validationRes).some((err) => err);
			if (hasErrors) {
				setValidationErrors(validationRes);
				emitToasterMsg(
					"warning",
					"Validation Error",
					"Please correct the highlighted fields before proceeding."
				);
				return false;
			}
			if (creatingRow) {
				await handleCreateZET({ values: editedValues, table }, true);
				return true;
			} else if (editingRow && isDirty) {
				await handleUpdateZET({ values: editedValues, table, row: editingRow }, true);
				return true;
			}
			if (!isDirty) {
				table.setEditingRow(null);
				return true;
			}
		}
	}));


	const handleCreateZET = async ({ values, table }, isAuto = false) => {
		const errors = validateZET(values);
		if (Object.keys(errors).length > 0) {
			setValidationErrors(errors);
			return;
		}

		const newEntry = {
			zetType: values.zetType,
			zetName: values.zetName?.trim(),
			serialNumber: values.serialNumber?.trim(),
			descriptionOfZet: values.descriptionOfZet?.trim(),
		};

		await createZET(newEntry, isAuto);
		table.setCreatingRow(null);
	};

	const handleUpdateZET = async ({ values, table, row }, isAuto = false) => {
		const errors = validateZET(values);
		if (Object.keys(errors).length > 0) {
			setValidationErrors(errors);
			return;
		}

		const updatedEntry = {
			zetType: values.zetType,
			zetName: values.zetName?.trim(),
			serialNumber: values.serialNumber?.trim(),
			descriptionOfZet: values.descriptionOfZet?.trim(),
		};

		await updateZET(row.index, updatedEntry, isAuto);
		table.setEditingRow(null);
	};


	const safeValue = (value) => (typeof value === "string" ? value : "");
	const safeLength = (value) => safeValue(value).length;

	const columns = useMemo(
		() => [
			{
				accessorKey: "zetType",
				header: "Zero-emission technology type",
				editVariant: "select",
				editSelectOptions: dropdownOptions.zetTypes,
				Cell: ({ cell }) => {
					const code = cell.getValue();
					const matched = dropdownOptions.zetTypes.find((option) => option.value === code);
					return matched ? matched.label : code;
				},
				muiEditTextFieldProps: () => ({
					required: true,
					error: !!validationErrors?.zetType,
					helperText: validationErrors?.zetType,
					onFocus: () => setValidationErrors((prev) => ({ ...prev, zetType: undefined })),
				}),
			},
			{
				accessorKey: "zetName",
				header: "Zero-emission technology name",
				muiEditTextFieldProps: ({ cell }) => {
					const value = safeValue(cell.getValue());
					return {
						required: false,
						defaultValue: value,
						inputProps: { maxLength: 1000 },
						error: !!validationErrors?.zetName,
						helperText: validationErrors?.zetName,
						FormHelperTextProps: { sx: { color: "#002A4E", fontSize: "0.57rem" } },
						onFocus: () => setValidationErrors((prev) => ({ ...prev, zetName: undefined })),
					};
				},
			},

			{
				accessorKey: "serialNumber",
				header: "Serial Number",
				muiEditTextFieldProps: ({ cell }) => {
					const value = safeValue(cell.getValue());
					return {
						required: false,
						defaultValue: value,
						inputProps: { maxLength: 1000 },
						error: !!validationErrors?.serialNumber,
						helperText: validationErrors?.serialNumber,
						FormHelperTextProps: { sx: { color: "#002A4E", fontSize: "0.57rem" } },
						onFocus: () => setValidationErrors((prev) => ({ ...prev, serialNumber: undefined })),
					};
				},
			},
			{
				accessorKey: "descriptionOfZet",
				header: "Technical description of zero-emission technology",
				muiEditTextFieldProps: ({ cell }) => {
					const value = safeValue(cell.getValue());
					return {
						required: false,
						defaultValue: value,
						inputProps: { maxLength: 1000 },
						error: !!validationErrors?.descriptionOfZet,
						helperText: validationErrors?.descriptionOfZet,
						FormHelperTextProps: { sx: { color: "#002A4E", fontSize: "0.57rem" } },
						onFocus: () => setValidationErrors((prev) => ({ ...prev, descriptionOfZet: undefined })),
					};
				},
			},
		],
		[dropdownOptions.zetTypes, validationErrors]
	);

	const table = useMaterialReactTable({
		columns,
		data: zetData || [],
		enableStickyHeader: true,
		enableSorting: false,
		enableRowSelection: false,
		enableColumnPinning: false,
		enableTopToolbar: true,
		enableBottomToolbar: false,
		enableColumnActions: false,
		enableGlobalFilter: false,
		enableColumnFilters: false,
		createDisplayMode: "row",
		editDisplayMode: "row",
		enableEditing: true,
		enablePagination: false,
		enableDensityToggle: false,
		enableFullScreenToggle: false,
		enableColumnVisibility: false,
		muiTableHeadCellProps: {
			sx: {
				color: "rgb(255 255 255)",
				backgroundColor: "#042a4c",
				position: "sticky",
				borderRight: "1px solid lightgray",
				"&:last-child": {
					borderRight: "none",
				},
			},
		},
		muiTablePaperProps: {
			sx: {
				boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
			},
		},
		muiTableContainerProps: {
			sx: {
				maxHeight: "520px",
			},
		},

		muiTableBodyCellProps: ({ row, cell }) => {
			return {
				sx: {
					borderRight: "1px solid lightgray",
					"&:last-child": {
						borderRight: "none",
					},
					color: "",
				},
			};
		},
		muiTableBodyRowProps: ({ row }) => ({
			sx: {
				"&:not(:last-of-type)": {
					borderBottom: "1px solid lightgray",
				},
				verticalAlign: "top",
			},
		}),
		muiTableHeadRowProps: {
			sx: {
				borderBottom: "2px solid lightgray",
			},
		},
		columnOrder: ["zetType", "zetName", "serialNumber", "descriptionOfZet"],
		renderToolbarInternalActions: () => null,
		onCreatingRowSave: handleCreateZET,
		onEditingRowSave: handleUpdateZET,
		onEditingRowCancel: resetActiveRow,
		onCreatingRowCancel: resetActiveRow,
		renderTopToolbar: ({ table }) => (
			<div style={{ padding: "8px", display: "flex", justifyContent: "flex-start" }}>
				<Button
					startIcon={<Icon icon={ICONS.ADD} />}
					onClick={() => handleFuelActions(table, null, RowActions.Add)}
					disabled={!!table.getState().creatingRow}
				>
					Add ZET
				</Button>
			</div>
		),

		renderRowActions: ({ row, table }) => (
			<Box sx={{ display: "flex", gap: "1rem" }}>
				<Tooltip title="Edit">
					<IconButton onClick={() => handleFuelActions(table, row, RowActions.Edit)}>
						<EditIcon />
					</IconButton>
				</Tooltip>
				<Tooltip title="Delete">
					<IconButton color="error" onClick={() => handleDeleteBtnClick(row?.index, true)}>
						<DeleteIcon />
					</IconButton>
				</Tooltip>
			</Box>
		),
	});

	return (
		<>
			<div className="fuel-registry-b3 mt-1">
				<MaterialReactTable table={table} />
			</div>
			<div className="confirmation-modal-container">
				<Modal
					isOpen={confirmDelete}
					modalHeader
					padding={"70px"}
					headerHeight={"50px"}
					showFooter
					onCloseClickListener={() => {
						setConfirmDelete(false);
					}}
					footer={
						<div className="d-flex justify-content-end fixed-action-btns p-4">
							<Button
								className="modal-delete-btn"
								onClick={() => { handleDeleteZet(deletingRowIndex) }}
								primaryColor={COLORS.RED.DARK}
								primaryBackgroundColor={COLORS.RED.LIGHT}
							>
								Delete
							</Button>
							<Button
								className="modal-cancel-btn"
								onClick={() => {
									setConfirmDelete(false);
								}}
							>
								Cancel
							</Button>
						</div>
					}
				>
					<div>Are you sure you want to delete this ZET?</div>
				</Modal>
			</div>
		</>
	);
});

export default FeuB5ZeroEmission;
